import type { ReactNode } from 'react'
import { cn } from '../cn'

export interface SkeletonProps {
  className?: string
}

/** Placeholder block. Decorative: wrap loading regions in `aria-busy` and give them a label. */
export function Skeleton({ className }: SkeletonProps) {
  return (
    <span
      aria-hidden="true"
      className={cn('block animate-pulse rounded-md bg-line-soft', className ?? 'h-4 w-full')}
    />
  )
}

export interface SkeletonTextProps {
  lines?: number
  className?: string
}

export function SkeletonText({ lines = 3, className }: SkeletonTextProps) {
  return (
    <span aria-hidden="true" className={cn('flex flex-col gap-2.5', className)}>
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton
          // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder rows
          key={index}
          className={cn('h-3.5', index === lines - 1 && lines > 1 ? 'w-3/5' : 'w-full')}
        />
      ))}
    </span>
  )
}

/** Labelled loading region for screen readers, with skeleton content inside. */
export function LoadingRegion({
  label = 'Loading…',
  children,
  className,
}: {
  label?: string
  children?: ReactNode
  className?: string
}) {
  return (
    <div role="status" aria-busy="true" aria-live="polite" className={className}>
      <span className="sr-only">{label}</span>
      {children ?? <SkeletonText />}
    </div>
  )
}
